import { useEffect } from "react";
import styled from "styled-components";
import { Flex, Text, Image } from "rebass/styled-components";
import { FaPlay } from "react-icons/fa";
import { useRecoilValue } from "recoil";

import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { RenderPreMatchState } from "MVVM/State/embeddablePlaylist";
import { formatNumber } from "MVVM/Utilities/Clip";
import {
  TopViewedPlayers,
  TopViewedMatches,
  TopViewedTeam,
  TopViewedPlayersList,
  TopViewedMatchesList,
  TopViewedTeamsList,
  TopViewedPlayersByCountryList,
  TopViewedPlayersByCountry,
} from "MVVM/Views/EmbeddablePlaylist/@types";

export const TopViewedListContainer = ({
  topViewedPlayers,
  topViewedPlayersByCountry,
  topViewedMatches,
  topViewedTeams,
  theme = "night",
}: {
  topViewedPlayers: TopViewedPlayersList | null;
  topViewedPlayersByCountry: TopViewedPlayersByCountryList | null;
  topViewedMatches: TopViewedMatchesList | null;
  topViewedTeams: TopViewedTeamsList | null;
  theme?: string;
}) => {
  const renderPreMatchState = useRecoilValue(RenderPreMatchState);
  const { page } = useAnalyticsContext();

  useEffect(() => {
    page("Loaded Trending Content List", {
      location: "embeddable_playlist",
      locationIdentifier: "trending_content_list",
      source: "website",
      platform: "HLTV.ORG",
    });
  }, [page]);

  return (
    <StyledContainer theme={theme} $renderPreMatchState={renderPreMatchState}>
      <StyledColumn>
        <StyledHeader theme={theme}>Trending Players</StyledHeader>
        {!!topViewedPlayers &&
          topViewedPlayers.map((player: TopViewedPlayers) => (
            <PlayerRow player={player} theme={theme} />
          ))}
      </StyledColumn>
      <StyledColumn className="rising-stars-column">
        {!!topViewedPlayersByCountry &&
          topViewedPlayersByCountry.length > 0 && (
            <>
              <StyledHeader theme={theme}>Rising Stars</StyledHeader>
              {topViewedPlayersByCountry.map(
                (player: TopViewedPlayersByCountry) => (
                  <PlayerRow player={player} theme={theme} />
                ),
              )}
            </>
          )}
        {!topViewedPlayersByCountry ||
          (topViewedPlayersByCountry.length === 0 && (
            <>
              <StyledHeader theme={theme}>Trending Teams</StyledHeader>
              {!!topViewedTeams &&
                topViewedTeams.map((team: TopViewedTeam) => (
                  <TeamRow team={team} theme={theme} />
                ))}
            </>
          ))}
      </StyledColumn>
      <StyledColumn className="matches-column">
        <StyledHeader theme={theme}>Most Viewed Matches</StyledHeader>
        {!!topViewedMatches &&
          topViewedMatches.map((match: TopViewedMatches) => (
            <MatchRow match={match} theme={theme} />
          ))}
      </StyledColumn>
    </StyledContainer>
  );
};

const StyledColumn = styled(Flex)`
  flex-direction: column;
  flex: 1;
  gap: 5px;
  height: 100%;
  overflow: hidden;
`;

const StyledContainer = styled(Flex)<{
  theme: string;
  $renderPreMatchState: boolean;
}>`
  background-color: ${(props) =>
    props.theme === "day" ? "#e5e7ea" : "#1b2024"};
  height: ${({ $renderPreMatchState }) =>
    $renderPreMatchState ? "194px" : "160px"};
  gap: 10px;
  overflow: ${({ $renderPreMatchState }) =>
    $renderPreMatchState ? "hidden" : "visible"};
  padding: 0px 5px 10px;
  width: 100%;

  ${({ $renderPreMatchState }) =>
    $renderPreMatchState
      ? `
      @media (max-width: 502px) {
        height: 150px;
      }

      @media (max-width: 380px) {
        height: 120px;
      }
    `
      : `
      @media (max-width: 360px) {
        height: 130px;
      }
    `}

  @media (max-width: 502px) {
    .rising-stars-column {
      display: none;
    }
  }

  @media (max-width: 360px) {
    .matches-column {
      display: none;
    }
  }
`;

const StyledHeader = styled(Text)<{ theme: string }>`
  color: ${(props) => (props.theme === "day" ? "#353535" : "#929a9e")};
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  padding: 6px 0 1px;
`;

export const PlayerRow = ({
  player,
  theme = "night",
}: {
  player: TopViewedPlayers | TopViewedPlayersByCountry;
  theme?: string;
}) => {
  const { track } = useAnalyticsContext();
  const ajs_aid = window.rudderanalytics.getAnonymousId();
  const platform = new URLSearchParams(window.location.search).get("platform");

  return (
    <StyledRow
      theme={theme}
      onClick={() => {
        track("Button - Click", {
          location: "trending_content_list",
          locationIdentifier: "top_player",
          source: "website",
          platform: "HLTV.ORG",
          clipId: player.clip,
          playlistId: player.playlist,
        });

        window.open(
          `${window.location.origin}/clip/${player.clip}?context=playlist&playlistId=${player.playlist}&tab=recommended&ajs_aid=${ajs_aid}&utm_source=${platform}`,
          "_blank",
        );
      }}
    >
      <Flex style={{ flex: "0 0 auto", alignItems: "center", gap: "5px" }}>
        <StyledCountryFlag
          src={`${
            process.env.REACT_APP_S3_CLIP_BUCKET
          }/static/web/flags/${player.metadata.playerCtryISO.toLowerCase()}.png`}
        />
        <StyledTeamLogo src={player.metadata.teamLogo} />
      </Flex>
      <Flex style={{ flex: "1" }}>
        <StyledText theme={theme}>{player.metadata.playerName}</StyledText>
      </Flex>
      <StyledViewsContainer>
        <PlayIcon />
        <StyledText theme={theme}>{formatNumber(player.views)}</StyledText>
      </StyledViewsContainer>
    </StyledRow>
  );
};

export const MatchRow = ({
  match,
  theme = "night",
}: {
  match: TopViewedMatches;
  theme?: string;
}) => {
  const { track } = useAnalyticsContext();
  const ajs_aid = window.rudderanalytics.getAnonymousId();
  const platform = new URLSearchParams(window.location.search).get("platform");
  const team1 = match.metadata[0];
  const team2 = match.metadata[1];

  return (
    <StyledRow
      theme={theme}
      onClick={() => {
        track("Button - Click", {
          location: "trending_content_list",
          locationIdentifier: "top_match",
          source: "website",
          platform: "HLTV.ORG",
          clipId: match.clip,
          playlistId: match.playlist,
        });

        window.open(
          `${window.location.origin}/clip/${match.clip}?context=playlist&playlistId=${match.playlist}&tab=recommended&ajs_aid=${ajs_aid}&utm_source=${platform}`,
          "_blank",
        );
      }}
    >
      <Flex style={{ flex: 1, gap: "5px" }}>
        <StyledTeamLogo src={team1.teamLogo} />
        <StyledText theme={theme}>{team1.teamName}</StyledText>
        <StyledText theme={theme} style={{ fontWeight: 400 }}>
          vs
        </StyledText>
        <StyledTeamLogo src={team2.teamLogo} />
        <StyledText theme={theme}>{team2.teamName}</StyledText>
      </Flex>
      <StyledViewsContainer>
        <PlayIcon />
        <StyledText theme={theme}>{formatNumber(match.views)}</StyledText>
      </StyledViewsContainer>
    </StyledRow>
  );
};

export const TeamRow = ({
  team,
  theme = "night",
}: {
  team: TopViewedTeam;
  theme?: string;
}) => {
  const { track } = useAnalyticsContext();
  const ajs_aid = window.rudderanalytics.getAnonymousId();
  const platform = new URLSearchParams(window.location.search).get("platform");
  return (
    <StyledRow
      theme={theme}
      style={{ gap: "10px", alignItems: "center" }}
      onClick={() => {
        track("Button - Click", {
          location: "trending_content_list",
          locationIdentifier: "rising_star",
          source: "website",
          platform: "HLTV.ORG",
          clipId: team.clip,
          playlistId: team.playlist,
        });

        window.open(
          `${window.location.origin}/clip/${team.clip}?context=playlist&playlistId=${team.playlist}&tab=recommended&ajs_aid=${ajs_aid}&utm_source=${platform}`,
          "_blank",
        );
      }}
    >
      <StyledTeamLogo
        src={team.metadata.teamLogo}
        style={{ flex: "0 0 auto" }}
      />
      <StyledText theme={theme} style={{ flex: "1" }}>
        {team.metadata.teamName}
      </StyledText>
      <StyledCountryFlag
        src={`${
          process.env.REACT_APP_S3_CLIP_BUCKET
        }/static/web/flags/${team.metadata.teamCtryISO.toLowerCase()}.png`}
        style={{ flex: "0 0 auto" }}
      />
    </StyledRow>
  );
};

const StyledRow = styled(Flex)<{ theme: string }>`
  background-color: ${(props) =>
    props.theme === "day" ? "#f6f6f6" : "#232d38"};
  cursor: pointer;
  gap: 5px;
  padding: 5px;
  box-shadow: ${(props) =>
    props.theme === "day" ? "0px 1px 2px 0 rgba(50, 50, 50, 0.4)" : "none"};

  &:nth-child(even) {
    background-color: ${(props) =>
      props.theme === "day" ? "#ffffff" : "#2d3844"};
  }

  :hover {
    background-color: ${(props) =>
      props.theme === "day" ? "#e0e0e0" : "#415064"};
  }
`;

const StyledViewsContainer = styled(Flex)`
  align-items: center;
  flex: 0 0 auto;
  gap: 5px;
  margin-left: auto;
`;

const StyledText = styled(Text)<{ theme: string }>`
  color: ${(props) => (props.theme === "day" ? "#333333" : "#929a9e")};
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledCountryFlag = styled(Image)`
  height: 8px;
  width: 12px;
`;

const StyledTeamLogo = styled(Image)`
  height: 12px;
  width: 12px;
`;

const PlayIcon = styled(FaPlay)`
  height: 6px;
  color: ${({ theme }) => theme.colors.envy};
  width: 6px;
`;
